import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import API, { formatApiError } from "../lib/api";
import {
  User, CreditCard, Users, HelpCircle, Loader2, Check, Trash2, Plus, Mail, X
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

export default function SettingsPage() {
  const { user } = useAuth();
  const [child, setChild] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [childForm, setChildForm] = useState({ name: "", age: "" });
  const [addParentOpen, setAddParentOpen] = useState(false);
  const [newParent, setNewParent] = useState({ email: "", name: "" });
  const [addingParent, setAddingParent] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [dashRes, subRes, parRes] = await Promise.all([
        API.get("/dashboard"),
        API.get("/subscription"),
        API.get("/parent-accounts"),
      ]);
      const c = dashRes.data.child;
      setChild(c);
      setChildForm({ name: c?.name || "", age: c?.age?.toString() || "" });
      setSubscription(subRes.data);
      setParents(parRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveChild = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await API.put("/child-profile", { name: childForm.name, age: parseInt(childForm.age) || child?.age });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const addParent = async () => {
    if (!newParent.email.trim() || !newParent.name.trim()) return;
    setAddingParent(true);
    try {
      const { data } = await API.post("/parent-accounts", newParent);
      setParents((prev) => [...prev, data]);
      setNewParent({ email: "", name: "" });
      setAddParentOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setAddingParent(false);
    }
  };

  const removeParent = async (id) => {
    setParents((prev) => prev.filter((p) => p.id !== id));
    try {
      await API.delete(`/parent-accounts/${id}`);
    } catch {
      fetchAll();
    }
  };

  const inputCls = "w-full bg-[#FFF0E0]/50 border-2 border-transparent focus:border-[#4D96FF] focus:bg-white rounded-2xl px-4 py-3 text-base font-semibold outline-none transition-all placeholder:text-[#52526B]/50";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#FF5C39] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold text-[#1F1F2E] tracking-tight">Settings</h1>
        <p className="text-sm font-semibold text-[#52526B] mt-1">Manage your account and preferences</p>
      </div>

      {/* Child Profile */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="child-profile-settings">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-[#FFD23F]/20 rounded-xl flex items-center justify-center">
            <User className="w-5 h-5 text-[#FFD23F]" strokeWidth={2.5} />
          </div>
          <h3 className="font-extrabold text-[#1F1F2E]">Child's profile</h3>
        </div>

        <div className="flex items-center gap-4 mb-5">
          <img
            src={child?.photo_url || "https://images.unsplash.com/photo-1597113117918-eb40fe0b84e7?w=200"}
            alt={child?.name}
            className="w-16 h-16 rounded-full object-cover border-3 border-[#FFD23F]"
          />
          <div className="flex-1">
            <p className="font-bold text-[#1F1F2E]">{child?.name}</p>
            <p className="text-sm text-[#52526B] font-semibold">Age {child?.age}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1 block">Name</label>
            <input
              value={childForm.name}
              onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
              data-testid="child-name-input"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1 block">Age</label>
            <input
              type="number"
              min="4"
              max="17"
              value={childForm.age}
              onChange={(e) => setChildForm({ ...childForm, age: e.target.value })}
              data-testid="child-age-input"
              className={inputCls}
            />
          </div>
          <button
            onClick={saveChild}
            disabled={saving}
            data-testid="save-child-profile-button"
            className={`w-full font-bold rounded-2xl px-6 py-3.5 active:scale-95 transition-all flex items-center justify-center gap-2 ${
              saved ? "bg-[#34D399] text-white" : "bg-[#FF5C39] text-white hover:bg-[#E04828] shadow-sm"
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <><Check className="w-4 h-4" strokeWidth={3} /> Saved!</> : "Save changes"}
          </button>
        </div>
      </div>

      {/* Subscription */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="subscription-settings">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-[#4D96FF]/10 rounded-xl flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-[#4D96FF]" strokeWidth={2.5} />
          </div>
          <h3 className="font-extrabold text-[#1F1F2E]">Subscription</h3>
        </div>

        <div className="bg-[#FFF0E0] rounded-2xl p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-[#52526B]">Plan</span>
            <span className="font-extrabold text-[#1F1F2E]">{subscription?.plan}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-[#52526B]">Price</span>
            <span className="font-extrabold text-[#FF5C39]">{subscription?.price}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-[#52526B]">Status</span>
            <span className="bg-[#34D399]/20 text-[#10B981] px-3 py-1 rounded-full text-xs font-bold">
              {subscription?.status}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-bold text-[#52526B]">Next billing</span>
            <span className="font-bold text-[#1F1F2E] text-sm">{subscription?.next_billing_date}</span>
          </div>
        </div>

        <button
          data-testid="cancel-subscription-button"
          className="w-full mt-4 bg-white text-[#F43F5E] font-bold rounded-2xl px-6 py-3 border-2 border-[#F43F5E]/20 hover:bg-[#F43F5E]/5 active:scale-95 transition-all text-sm"
        >
          Cancel subscription
        </button>
      </div>

      {/* Parent Accounts */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="parent-accounts-settings">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#34D399]/10 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-[#34D399]" strokeWidth={2.5} />
            </div>
            <h3 className="font-extrabold text-[#1F1F2E]">Parent accounts</h3>
          </div>
          <Dialog open={addParentOpen} onOpenChange={setAddParentOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="add-parent-button"
                className="bg-[#FF5C39] text-white font-bold rounded-xl px-3 py-2 text-xs hover:bg-[#E04828] active:scale-95 transition-all flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                Add
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-[32px] border-[#F0E4D8]">
              <DialogHeader>
                <DialogTitle className="text-xl font-extrabold text-[#1F1F2E]">Add a parent</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">Name</label>
                  <input value={newParent.name} onChange={(e) => setNewParent({ ...newParent, name: e.target.value })} data-testid="add-parent-name-input" className={inputCls} placeholder="Parent's name" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#52526B] mb-1.5 block">Email</label>
                  <input type="email" value={newParent.email} onChange={(e) => setNewParent({ ...newParent, email: e.target.value })} data-testid="add-parent-email-input" className={inputCls} placeholder="parent@email.com" />
                </div>
                <button
                  onClick={addParent}
                  disabled={addingParent}
                  data-testid="add-parent-submit-button"
                  className="w-full bg-[#FF5C39] text-white font-bold rounded-2xl px-6 py-3.5 hover:bg-[#E04828] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {addingParent && <Loader2 className="w-4 h-4 animate-spin" />}
                  Add parent
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-2">
          {parents.map((p) => (
            <div key={p.id || p.email} className="flex items-center gap-3 p-3 rounded-xl bg-[#FFF0E0]/50" data-testid={`parent-account-${p.email}`}>
              <div className="w-10 h-10 bg-[#4D96FF] rounded-full flex items-center justify-center text-white font-extrabold text-sm">
                {p.name?.charAt(0)?.toUpperCase() || "P"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm text-[#1F1F2E] truncate">{p.name}</p>
                <p className="text-xs text-[#52526B] font-semibold truncate">{p.email}</p>
              </div>
              {p.is_primary ? (
                <span className="text-xs font-bold text-[#4D96FF] bg-[#4D96FF]/10 px-2 py-1 rounded-full">Primary</span>
              ) : (
                <button
                  onClick={() => removeParent(p.id)}
                  data-testid={`remove-parent-${p.email}`}
                  className="text-[#52526B] hover:text-[#F43F5E] transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Support */}
      <div className="bg-white rounded-[32px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#F0E4D8]/50" data-testid="contact-support-settings">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#FF5C39]/10 rounded-xl flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-[#FF5C39]" strokeWidth={2.5} />
          </div>
          <h3 className="font-extrabold text-[#1F1F2E]">Need help?</h3>
        </div>
        <p className="text-sm text-[#52526B] font-semibold mb-4">
          Our team is here to help with any questions about Alfie.
        </p>
        <a
          href="mailto:support@alfie.com"
          data-testid="contact-support-button"
          className="inline-flex items-center gap-2 bg-white text-[#1F1F2E] font-bold rounded-2xl px-6 py-3 border-2 border-[#F0E4D8] hover:border-[#4D96FF] hover:text-[#4D96FF] active:scale-95 transition-all text-sm"
        >
          <Mail className="w-4 h-4" strokeWidth={2.5} />
          Email support
        </a>
      </div>
    </div>
  );
}
